import { IconAngleLeft, IconAngleRight, IconClock, mapper } from "@maia/icons";
import {
  BigPricingPill,
  ButtonLink,
  Divider,
  FeaturedIcon,
  ProductIntro,
  Stepper,
  TextButton,
  Typography,
} from "@maia/ui";
import { formatCurrency } from "@maia/utils";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  linkOptions,
  Outlet,
  useMatches,
  useRouter,
} from "@tanstack/react-router";
import { getProductsOptions } from "../../../../features/edit-insurance/get-products/queryOptionts";
import { getCustomerOptions } from "../../../../features/customer/customer/queryOptions";

export const Route = createFileRoute("/_authenticated/edit-insurance/$customer/cart/$cart/_layout")(
  {
    component: RouteComponent,
    loader: async ({ context: { queryClient }, params: { customer: customerGuid, cart } }) => {
      const products = await queryClient.ensureQueryData(
        getProductsOptions({
          cartGuid: cart,
          customerGuid: customerGuid,
        }),
      );

      const customer = await queryClient.ensureQueryData(getCustomerOptions(customerGuid));

      return { products, customer };
    },
  },
);

function RouteComponent() {
  const params = Route.useParams();
  const { customer, products } = Route.useLoaderData();
  const matches = useMatches();
  const router = useRouter();

  const { data: product } = useQuery({
    ...getProductsOptions({
      cartGuid: params.cart,
      customerGuid: params.customer,
    }),
    initialData: products,
    select: (data) => data[0],
  });

  const isCompositionPage =
    matches.find((match) => match.fullPath === "/edit-insurance/$customer/cart/$cart/compositions")
      ?.status === "success";

  return (
    <div className="layout-sidebar">
      <div className="area-page-content content-grid">
        <div className="-ml-32 -mr-32 col-span-full">
          <div className="area-page-header px-32 flex items-center justify-between">
            <TextButton
              as="link"
              onPress={() => router.history.back()}
              intent="standard"
              size="tiny"
              iconLeft={IconAngleLeft}
            >
              Terug
            </TextButton>
          </div>
          <ProductIntro
            title={product.name}
            icon={mapper(product?.icon)}
            // onClickMoreInformation={alert}
            className="px-32 pt-24 pb-32"
          />
          {product.isComposition && (
            <Stepper
              className="py-24 border-y border-grayscale100"
              links={[
                linkOptions({
                  to: "/edit-insurance/$customer/cart/$cart",
                  params: params,
                  label: "Premiebepalende factoren",
                }),
                linkOptions({
                  to: "/edit-insurance/$customer/cart/$cart/compositions",
                  params: params,
                  label: "Configureren",
                }),
              ]}
            />
          )}
        </div>
        <div className="col-span-full">
          <Outlet />
        </div>
      </div>
      <div className="flex flex-col area-page-sidebar">
        <Typography variant="display6" weight="medium" className="text-primaryMain mb-32">
          Verzekering configureren
        </Typography>
        <div className="mb-12">
          <div className="flex items-center gap-x-12">
            {product?.price.premiumAfterTax ? (
              <div className="flex flex-col">
                <BigPricingPill
                  price={Number(product?.totalPrice ?? "0")}
                  variant="ghost"
                  size="lg"
                  billingUnit={customer.billingUnit}
                />
                <Typography variant="body-xs" weight="regular" className="text-grayscale400">
                  Incl. assurantiebelasting van {formatCurrency(Number(product.totalTax ?? "0"))}
                </Typography>
              </div>
            ) : (
              <div className="flex flex-col gap-y-8">
                <div className="flex gap-x-12">
                  <IconClock className="size-32 fill-info300" />
                  <Typography variant="display5" weight="semibold" className="text-info300">
                    Wachten op gegevens
                  </Typography>
                </div>
                <Typography variant="body-xs" weight="regular" className="text-grayscale300">
                  Vul alle gegevens in om de premie te berekenen.
                </Typography>
              </div>
            )}
          </div>
        </div>
        <Divider className="-ml-32 -mr-32 mb-12" />
        <ul className="flex flex-col gap-y-24 mb-12 grow">
          <li key={product.guid}>
            <div className="flex items-center gap-x-8 mb-8">
              <FeaturedIcon icon={mapper(product.icon)} variant="light-square" size="sm" />
              <Typography variant="body-xs" weight="semibold">
                {product.name}
              </Typography>
            </div>
            <div>
              <ul className="flex flex-col gap-y-8">
                {product.addOns
                  ?.filter((addon) => addon.answer.value === "J")
                  .map((addon) => (
                    <li key={addon.guid} className="flex flex-col gap-y-4">
                      <Typography
                        key={addon.guid}
                        variant="body-xs"
                        weight="medium"
                        className="text-grayscale500"
                      >
                        {addon.phrasing}
                      </Typography>
                      {addon.children?.map((child) => (
                        <Typography
                          key={addon.guid}
                          variant="body-xs"
                          weight="regular"
                          className="text-grayscale400"
                        >
                          {
                            child.options.find((option) => option.value === child.answer.value)
                              ?.label
                          }
                        </Typography>
                      ))}
                    </li>
                  ))}
              </ul>
            </div>
          </li>
        </ul>
        <Divider className="-ml-32 -mr-32 mb-12" />
        <div>
          <div className="flex justify-between">
            <Typography variant="body-s" weight="medium">
              Totaal {customer.billingUnit.label}
            </Typography>
            <Typography variant="body-s" weight="medium">
              {formatCurrency(Number(product.totalPrice ?? "0"))}
            </Typography>
          </div>
          <div className="flex justify-between mb-24">
            <Typography variant="body-xs" weight="regular" className="text-grayscale400">
              Assurantiebelasting
            </Typography>
            <Typography variant="body-xs" weight="regular" className="text-grayscale400">
              {formatCurrency(Number(product.totalTax ?? "0"))}
            </Typography>
          </div>
          {isCompositionPage && (
            <ButtonLink
              to="/edit-insurance/$customer/cart/$cart/summary"
              params={params}
              intent="primary"
              iconRight={IconAngleRight}
              className="mt-auto w-full justify-center"
              isDisabled={!isCompositionPage}
            >
              Volgende
            </ButtonLink>
          )}
        </div>
      </div>
    </div>
  );
}
